import {
  CategoriaNoteDto,
  CategoriaNoteListDto,
  Configuration,
  ControllerPerLaGestioneDeiRuoliDiAccessoEDelleAssociazioniUtenteRuoloApi,
  ControllerPerLeFunzioniDedicateAlCicloPassivoApi,
  ControllerProjectApi,
  ControllerUserApi,
  OrderCategoryDto,
  OrderTypeDto,
  OrderTypologyDto,
  ProjectStatusDto,
  ProjectTypeDto,
  RuoloAccessoInputDto,
  RuoloAccessoOutputDto,
  UserDto,
  UtenteRuoloAccessoInputDto,
  UtenteRuoloAccessoOutputDto,
} from '@api/client';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ThunkApiConfig } from 'core/store';
import getConfiguration, { errorHandler } from 'core/store/config';

enum SettingsActions {
  // Note Categories
  getNoteCategories = '[Settings] Get Note Categories',
  saveNoteCategory = '[Settings] Save Note Category',
  deleteNoteCategory = '[Settings] Delete Note Category',
  // Project Types
  getProjectTypes = '[Settings] Get Project Types',
  saveProjectType = '[Settings] Save Project Type',
  deleteProjectType = '[Settings] Delete Project Type',
  // Project Statuses
  getProjectStatuses = '[Settings] Get Project Statuses',
  saveProjectStatus = '[Settings] Save Project Status',
  deleteProjectStatus = '[Settings] Delete Project Status',
  // Order Categories
  getOrderCategories = '[Settings] Get Order Categories',
  saveOrderCategory = '[Settings] Save Order Category',
  deleteOrderCategory = '[Settings] Delete Order Category',
  // Order Types
  getOrderTypes = '[Settings] Get Order Types',
  saveOrderType = '[Settings] Save Order Type',
  deleteOrderType = '[Settings] Delete Order Type',
  // Order Typologies
  getOrderTypologies = '[Settings] Get Order Typologies',
  saveOrderTypology = '[Settings] Save Order Typology',
  deleteOrderTypology = '[Settings] Delete Order Typology',
  // Ruolo Accesso
  getRuoloAccessoList = '[Settings] Get Ruolo Accesso List',
  saveRuoloAccesso = '[Settings] Save Ruolo Accesso',
  deleteRuoloAccesso = '[Settings] Delete Ruolo Accesso',
  // Utente Ruolo Accesso
  getUtenteRuoloAccessoList = '[Settings] Get Utente Ruolo Accesso List',
  saveUtenteRuoloAccesso = '[Settings] Save Utente Ruolo Accesso',
  deleteUtenteRuoloAccesso = '[Settings] Delete Utente Ruolo Accesso',
  // Users
  getUsersList = '[Settings] Get Users List',
}

const api = (extra: any, getState: any) =>
  new ControllerProjectApi(getConfiguration(Configuration, extra, getState));

const passiveApi = (extra: any, getState: any) =>
  new ControllerPerLeFunzioniDedicateAlCicloPassivoApi(getConfiguration(Configuration, extra, getState));

const ruoloAccessoApi = (extra: any, getState: any) =>
  new ControllerPerLaGestioneDeiRuoliDiAccessoEDelleAssociazioniUtenteRuoloApi(
    getConfiguration(Configuration, extra, getState),
  );

const userApi = (extra: any, getState: any) =>
  new ControllerUserApi(getConfiguration(Configuration, extra, getState));

// Note Categories
export const getNoteCategories = createAsyncThunk<CategoriaNoteListDto | undefined, void, ThunkApiConfig>(
  SettingsActions.getNoteCategories,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await api(extra, getState).getNoteCategories();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const saveNoteCategory = createAsyncThunk<CategoriaNoteDto | undefined, CategoriaNoteDto, ThunkApiConfig>(
  SettingsActions.saveNoteCategory,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await api(extra, getState).saveCategoriaNote({ categoriaNoteDto: payload });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const deleteNoteCategory = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteNoteCategory,
  async (id, { dispatch, extra, getState }) => {
    try {
      await api(extra, getState).deleteCategoriaNote({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Project Types
export const getProjectTypesList = createAsyncThunk<ProjectTypeDto[] | undefined, void, ThunkApiConfig>(
  SettingsActions.getProjectTypes,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await api(extra, getState).getProjectTypes();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const saveProjectType = createAsyncThunk<ProjectTypeDto | undefined, ProjectTypeDto, ThunkApiConfig>(
  SettingsActions.saveProjectType,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await api(extra, getState).saveProjectType({ projectTypeDto: payload });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const deleteProjectType = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteProjectType,
  async (id, { dispatch, extra, getState }) => {
    try {
      await api(extra, getState).deleteProjectType({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Project Statuses
export const getProjectStatusesList = createAsyncThunk<ProjectStatusDto[] | undefined, void, ThunkApiConfig>(
  SettingsActions.getProjectStatuses,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await api(extra, getState).getProjectStatuses();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const saveProjectStatus = createAsyncThunk<ProjectStatusDto | undefined, ProjectStatusDto, ThunkApiConfig>(
  SettingsActions.saveProjectStatus,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await api(extra, getState).saveProjectStatus({ projectStatusDto: payload });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const deleteProjectStatus = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteProjectStatus,
  async (id, { dispatch, extra, getState }) => {
    try {
      await api(extra, getState).deleteProjectStatus({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Order Categories
export const getOrderCategoriesList = createAsyncThunk<OrderCategoryDto[] | undefined, void, ThunkApiConfig>(
  SettingsActions.getOrderCategories,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await passiveApi(extra, getState).getOrderCategories();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const saveOrderCategory = createAsyncThunk<OrderCategoryDto | undefined, OrderCategoryDto, ThunkApiConfig>(
  SettingsActions.saveOrderCategory,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await passiveApi(extra, getState).saveOrderCategory({ orderCategoryDto: payload });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const deleteOrderCategory = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteOrderCategory,
  async (id, { dispatch, extra, getState }) => {
    try {
      await passiveApi(extra, getState).deleteOrderCategory({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Order Types
export const getOrderTypesList = createAsyncThunk<OrderTypeDto[] | undefined, void, ThunkApiConfig>(
  SettingsActions.getOrderTypes,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await passiveApi(extra, getState).getOrderTypes();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const saveOrderType = createAsyncThunk<OrderTypeDto | undefined, OrderTypeDto, ThunkApiConfig>(
  SettingsActions.saveOrderType,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await passiveApi(extra, getState).saveOrderType({ orderTypeDto: payload });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const deleteOrderType = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteOrderType,
  async (id, { dispatch, extra, getState }) => {
    try {
      await passiveApi(extra, getState).deleteOrderType({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Order Typologies
export const getOrderTypologiesList = createAsyncThunk<OrderTypologyDto[] | undefined, void, ThunkApiConfig>(
  SettingsActions.getOrderTypologies,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await passiveApi(extra, getState).getOrderTypologies();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const saveOrderTypology = createAsyncThunk<OrderTypologyDto | undefined, OrderTypologyDto, ThunkApiConfig>(
  SettingsActions.saveOrderTypology,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await passiveApi(extra, getState).saveOrderTypology({ orderTypologyDto: payload });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const deleteOrderTypology = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteOrderTypology,
  async (id, { dispatch, extra, getState }) => {
    try {
      await passiveApi(extra, getState).deleteOrderTypology({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Ruolo Accesso
export const getRuoloAccessoList = createAsyncThunk<RuoloAccessoOutputDto[] | undefined, void, ThunkApiConfig>(
  SettingsActions.getRuoloAccessoList,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await ruoloAccessoApi(extra, getState).getAllRuoloAccesso();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

export const saveRuoloAccesso = createAsyncThunk<
  RuoloAccessoOutputDto | undefined,
  RuoloAccessoInputDto,
  ThunkApiConfig
>(SettingsActions.saveRuoloAccesso, async (payload, { dispatch, extra, getState }) => {
  try {
    return await ruoloAccessoApi(extra, getState).saveRuoloAccesso({ ruoloAccessoInputDto: payload });
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const deleteRuoloAccesso = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteRuoloAccesso,
  async (id, { dispatch, extra, getState }) => {
    try {
      await ruoloAccessoApi(extra, getState).deleteRuoloAccesso({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Utente Ruolo Accesso
export const getUtenteRuoloAccessoList = createAsyncThunk<
  UtenteRuoloAccessoOutputDto[] | undefined,
  void,
  ThunkApiConfig
>(SettingsActions.getUtenteRuoloAccessoList, async (_, { dispatch, extra, getState }) => {
  try {
    return await ruoloAccessoApi(extra, getState).getAllUtenteRuoloAccesso();
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const saveUtenteRuoloAccesso = createAsyncThunk<
  UtenteRuoloAccessoOutputDto | undefined,
  UtenteRuoloAccessoInputDto,
  ThunkApiConfig
>(SettingsActions.saveUtenteRuoloAccesso, async (payload, { dispatch, extra, getState }) => {
  try {
    return await ruoloAccessoApi(extra, getState).saveUtenteRuoloAccesso({ utenteRuoloAccessoInputDto: payload });
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const deleteUtenteRuoloAccesso = createAsyncThunk<void, number, ThunkApiConfig>(
  SettingsActions.deleteUtenteRuoloAccesso,
  async (id, { dispatch, extra, getState }) => {
    try {
      await ruoloAccessoApi(extra, getState).deleteUtenteRuoloAccesso({ id });
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);

// Users
export const getUsersList = createAsyncThunk<UserDto[] | undefined, void, ThunkApiConfig>(
  SettingsActions.getUsersList,
  async (_, { dispatch, extra, getState }) => {
    try {
      const res = await userApi(extra, getState).searchUsers({ userSearchRequest: { attivo: true } });
      return res.utenti ?? [];
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  },
);
